import React, { useRef, useState } from "react"
import createPersistedState from "use-persisted-state"

import { parseProps, boxCreateApp, boxRevokeToken } from "./utils.js"

import View from "./CreateAppModal"

/**
 * A modal UI used to create a new app
 */
const CreateAppModal = ({
  id, onCreated, onToggleModal, ...props
}) => {
  // start with a default state by parsing the props
  const [state, setState] = useState({
    ...parseProps(props),
    processing: false,
  })

  // keep track of the persisted state for both the Box credentials and the
  // eventual client ID and secret we want to create
  const useBoxCredentials = createPersistedState(
    "com.box.developer.credentials",
  )
  const useCustomClientId = createPersistedState(
    `com.box.developer.${id}_credentials.client_id`,
  )
  const useCustomClientSecret = createPersistedState(
    `com.box.developer.${id}_credentials.client_secret`,
  )
  const [boxCredentials, setBoxCredentials] = useBoxCredentials({})
  const setCustomClientId = useCustomClientId({})[1]
  const setCustomClientSecret = useCustomClientSecret({})[1]

  // create a reference to the name field
  const nameRef = useRef(null)
  // helper method for updating state
  const updateState = (n) => setState((o) => ({ ...o, ...n }))

  // handle the createApp callback
  const createApp = async () => {
    // set the UI to processing
    updateState({ processing: true })
    // call the API to create the app
    const response = await boxCreateApp({ ...state, nameRef, boxCredentials })

    // store the new client ID and secret
    setCustomClientId(response.data.client_id)
    setCustomClientSecret(response.data.client_secret)
    // log out the user from box by revoking their token
    logOut()
    // update the state to no longer processing
    updateState({ processing: false })
    // notify the parent button that credentials have been created
    onCreated()
  }

  // handle log out from the Box app
  const logOut = () => {
    // revoke the access token so it can't be used to create more apps
    boxRevokeToken({ boxCredentials, useCreateAppScope: true })
    // clear the credentials from local storage
    setBoxCredentials({})
  }

  return (
    <View
      {...state}
      nameRef={nameRef}
      onToggleModal={onToggleModal}
      onCreateApp={createApp}
      onLogOut={logOut}
    />
  )
}

export default CreateAppModal
